import React from 'react';
import { Wifi, WifiOff } from 'lucide-react';
import ConfigurationStateSummary from './ConfigurationStateSummary';
import ConnectorStatusBadge from './ConnectorStatusBadge';
import ResourceAssignmentPanel from './ResourceAssignmentPanel';
import { formatDateTime, formatReportedStatus } from '../utils';

function Field({ label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-content-subtle">{label}</p>
      <p
        className={`text-sm text-content-primary mt-0.5 break-all ${
          mono ? 'font-mono text-xs' : ''
        }`}
      >
        {value || '—'}
      </p>
    </div>
  );
}

function EdgeConnectorCard({
  connector,
  canManage,
  busyAction,
  onReissueToken,
  onRotateSecret,
  onAbortRotation,
  onToggleBlocked,
  onRevoke,
  onDelete,
  onResourcesSaved,
}) {
  const isBusy = Boolean(busyAction);
  const isRevoked = connector.status === 'revoked';
  const isBlocked = connector.status === 'blocked';

  return (
    <div className="surface-card p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-content-primary">{connector.name}</p>
            <ConnectorStatusBadge status={connector.status} />
            {connector.registered && (
              <span
                className="inline-flex items-center gap-1 text-xs text-content-muted"
                title={
                  connector.online
                    ? 'Heartbeat recente'
                    : 'Sem heartbeat na janela configurada'
                }
              >
                {connector.online ? (
                  <Wifi className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                ) : (
                  <WifiOff className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                )}
                {connector.online ? 'online' : 'offline'}
              </span>
            )}
          </div>
          {connector.reported_status && (
            <p
              className="text-xs text-content-muted mt-1"
              title="Como o próprio Edge avalia sua operação, independente do estado da credencial no Core."
            >
              O Edge se reporta como {formatReportedStatus(connector.reported_status)}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="client_id" value={connector.client_id} mono />
        <Field label="Versão do Edge" value={connector.software_version} />
        <Field label="Protocolo" value={connector.protocol_version} />
        <Field label="Último heartbeat" value={formatDateTime(connector.last_heartbeat_at)} />
      </div>

      <ConfigurationStateSummary configuration={connector.configuration} />

      {connector.capabilities?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {connector.capabilities.map((capability) => (
            <span
              key={capability}
              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono bg-slate-100 text-slate-600 border border-slate-200"
            >
              {capability}
            </span>
          ))}
        </div>
      )}

      {connector.provisioning_token_pending && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Token de provisionamento emitido e ainda não usado. Ele expira em{' '}
          {formatDateTime(connector.provisioning_token_expires_at)}.
        </p>
      )}

      {connector.rotation_pending && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Rotação aguardando confirmação do connector até{' '}
          {formatDateTime(connector.rotation_expires_at)}. Sem confirmação, o segredo
          anterior continua sendo o único válido.
        </p>
      )}

      {canManage && !isRevoked && (
        <div className="flex flex-wrap gap-3 pt-1">
          {!connector.registered && (
            <button
              type="button"
              onClick={() => onReissueToken(connector)}
              disabled={isBusy}
              className="text-sm text-forge-600 hover:text-forge-800 disabled:opacity-50"
            >
              {busyAction === 'token' ? 'Emitindo...' : 'Emitir novo token'}
            </button>
          )}
          {connector.registered && (
            <button
              type="button"
              onClick={() => onRotateSecret(connector)}
              disabled={isBusy}
              className="text-sm text-forge-600 hover:text-forge-800 disabled:opacity-50"
            >
              {busyAction === 'rotate' ? 'Rotacionando...' : 'Rotacionar segredo'}
            </button>
          )}
          {connector.rotation_pending && (
            <button
              type="button"
              onClick={() => onAbortRotation(connector)}
              disabled={isBusy}
              className="text-sm text-content-muted hover:text-content-primary disabled:opacity-50"
            >
              {busyAction === 'abort' ? 'Desfazendo...' : 'Desfazer rotação'}
            </button>
          )}
          <button
            type="button"
            onClick={() => onToggleBlocked(connector, !isBlocked)}
            disabled={isBusy}
            className="text-sm text-content-muted hover:text-content-primary disabled:opacity-50"
          >
            {busyAction === 'block'
              ? 'Salvando...'
              : isBlocked
                ? 'Desbloquear'
                : 'Bloquear'}
          </button>
          <button
            type="button"
            onClick={() => onRevoke(connector)}
            disabled={isBusy}
            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            Revogar
          </button>
        </div>
      )}

      {!isRevoked && (
        <ResourceAssignmentPanel
          connector={connector}
          canManage={canManage}
          onSaved={onResourcesSaved}
        />
      )}

      {canManage && isRevoked && (
        <div className="flex flex-wrap gap-3 pt-1">
          <p className="text-xs text-content-muted">
            Revogado em {formatDateTime(connector.revoked_at)}. Para voltar a operar,
            crie outro connector.
          </p>
          <button
            type="button"
            onClick={() => onDelete(connector)}
            disabled={isBusy}
            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            Remover do painel
          </button>
        </div>
      )}
    </div>
  );
}

export default EdgeConnectorCard;
